import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { useLoggedInUser } from "components/LoggedInUserProvider";
import {
  TSupportedEntity,
  createListHorsesRequest,
  createListStallApplicationsRequest,
  createListUpcomingRaceEntriesRequest,
  createListWorkoutRequestsRequest,
  createPullHorsesOrTrainersRequest,
  getUniqueHorsesArrayFromEntities
} from "utils/app-search-utils";
import { getRequiredValue } from "utils/required-values-utils";
import { useRacehorse360Api } from "./api";
import { useInfiniteScroll } from "./useInfiniteScroll";

export const useAppSearch = (
  searchValue: string,
  isListOpened: boolean,
  showFullList: boolean,
  shouldMakeHorseOrTrainerRequest: boolean,
  stallApplicationsFilter: racehorse360.IStallApplicationFilter,
  workoutRequestsFilter: racehorse360.IWorkoutRequestFilter,
  upcomingRaceEntriesFilter: racehorse360.IUpcomingRaceEntryFilter,
  infiniteScrollRootRef: React.RefObject<HTMLElement>
) => {
  const { currentUser } = useLoggedInUser();

  const isNumericInput = !Number.isNaN(Number(searchValue));

  let facilityIds = null;
  if (currentUser.isRacingOfficial) {
    facilityIds = currentUser.racingOfficialFacilityIds;
  }

  let shouldSearchByChipNumber = false;
  let shouldRequestHorsesAndTrainers = false;
  let shouldRequestStallApplications = false;
  let shouldRequestWorkoutRequests = false;
  let shouldRequestRaceEntries = false;
  let shouldRequestHorses = false;
  let shouldRequestLimitedHorses = false;
  let shouldRequestInfiniteHorses = false;

  if (isListOpened) {
    shouldSearchByChipNumber = isNumericInput;
    shouldRequestHorsesAndTrainers = [
      shouldMakeHorseOrTrainerRequest,
      !shouldSearchByChipNumber
    ].every(Boolean);
    shouldRequestStallApplications = Boolean(stallApplicationsFilter);
    shouldRequestWorkoutRequests = Boolean(workoutRequestsFilter);
    shouldRequestRaceEntries = Boolean(upcomingRaceEntriesFilter);
    shouldRequestHorses = [
      !shouldRequestHorsesAndTrainers || shouldSearchByChipNumber,
      !shouldRequestStallApplications,
      !shouldRequestWorkoutRequests,
      !shouldRequestRaceEntries
    ].every(Boolean);
    shouldRequestLimitedHorses = [shouldRequestHorses, !showFullList].every(
      Boolean
    );
    shouldRequestInfiniteHorses = [shouldRequestHorses, showFullList].every(
      Boolean
    );
  }

  const {
    useListHorses,
    useInfiniteListHorses,
    useListWorkoutRequests,
    usePullHorsesOrTrainersByName,
    useListStallApplications,
    useListUpcomingRaceEntries
  } = useRacehorse360Api();

  const listHorsesRequest = shouldRequestHorses
    ? createListHorsesRequest(
        searchValue,
        facilityIds,
        shouldSearchByChipNumber
      )
    : null;

  const {
    data: horsesListData,
    isLoading: isHorsesLoading,
    isFetching: isHorsesFetching
  } = useListHorses(
    {
      ...listHorsesRequest
    },
    {
      refetchOnWindowFocus: false,
      enabled: [listHorsesRequest, shouldRequestLimitedHorses].every(Boolean)
    }
  );

  const {
    data: infiniteHorsesListData,
    isLoading: isInfiniteHorsesLoading,
    isFetching: isInfiniteHorsesFetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage
  } = useInfiniteListHorses(
    {
      ...listHorsesRequest
    },
    {
      enabled: [listHorsesRequest, shouldRequestInfiniteHorses].every(Boolean)
    }
  );
  useInfiniteScroll(
    infiniteScrollRootRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    shouldRequestInfiniteHorses
  );

  const pullHorsesOrTrainersRequest = shouldRequestHorsesAndTrainers
    ? createPullHorsesOrTrainersRequest(searchValue, facilityIds)
    : null;
  const {
    data: horseAndTrainersData,
    isLoading: areTrainersAndHorsesLoading,
    isFetching: areTrainersAndHorsesFetching
  } = usePullHorsesOrTrainersByName(
    {
      ...pullHorsesOrTrainersRequest
    },
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(pullHorsesOrTrainersRequest)
    }
  );

  const listStallApplicationsRequest = shouldRequestStallApplications
    ? createListStallApplicationsRequest(searchValue, stallApplicationsFilter)
    : null;
  const {
    data: stallApplicationsData,
    isLoading: isListStallApplicationsLoading,
    isFetching: isListStallApplicationsFetching
  } = useListStallApplications(
    {
      ...listStallApplicationsRequest
    },
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(listStallApplicationsRequest)
    }
  );

  const listUpcomingRaceEntriesRequest = shouldRequestRaceEntries
    ? createListUpcomingRaceEntriesRequest(
        searchValue,
        upcomingRaceEntriesFilter,
        shouldSearchByChipNumber
      )
    : null;
  const {
    data: raceEntriesData,
    isLoading: isRaceEntriesListLoading,
    isFetching: isRaceEntriesListFetching
  } = useListUpcomingRaceEntries(
    {
      ...listUpcomingRaceEntriesRequest
    },
    {
      enabled: Boolean(listUpcomingRaceEntriesRequest)
    }
  );

  const listWorkoutRequestsRequest = shouldRequestWorkoutRequests
    ? createListWorkoutRequestsRequest(
        searchValue,
        workoutRequestsFilter,
        shouldSearchByChipNumber
      )
    : null;
  const {
    data: workoutRequestsData,
    isLoading: isWorkoutRequestsLoading,
    isFetching: isWorkoutRequestsFetching
  } = useListWorkoutRequests(
    {
      ...listWorkoutRequestsRequest
    },
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(listWorkoutRequestsRequest)
    }
  );

  const isLoading = [
    isWorkoutRequestsLoading,
    isWorkoutRequestsFetching,
    isHorsesLoading,
    isHorsesFetching,
    areTrainersAndHorsesLoading,
    areTrainersAndHorsesFetching,
    isListStallApplicationsLoading,
    isListStallApplicationsFetching,
    isRaceEntriesListLoading,
    isRaceEntriesListFetching,
    isInfiniteHorsesLoading && !isFetchingNextPage,
    isInfiniteHorsesFetching && !isFetchingNextPage
  ].some(Boolean);

  let items: TSupportedEntity[];

  if (horsesListData?.horses) {
    items = horsesListData.horses;
  } else if (infiniteHorsesListData?.pages) {
    items = infiniteHorsesListData.pages.flat();
  } else if (horseAndTrainersData?.horsesOrTrainers) {
    items = getRequiredValue(
      horseAndTrainersData?.horsesOrTrainers?.map(
        item => item.horse ?? item.trainer
      ),
      []
    );
  } else if (stallApplicationsData?.stallApplications) {
    items = getRequiredValue(stallApplicationsData?.stallApplications, []);
  } else if (workoutRequestsData?.workoutRequests) {
    items = getUniqueHorsesArrayFromEntities(
      workoutRequestsData?.workoutRequests
    );
  } else if (raceEntriesData?.upcomingRaceEntries) {
    items = getUniqueHorsesArrayFromEntities(
      raceEntriesData?.upcomingRaceEntries
    );
  } else {
    items = [];
  }

  return { items, isLoading, isFetchingNextPage };
};
